import React from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  .hr-page * { box-sizing: border-box; margin: 0; padding: 0; }
  .hr-page { font-family: 'Inter', sans-serif; color: #0f172a; background: #ffffff; width: 100%; }

  /* ── Hero ── */
  .hr-hero { position: relative; padding: 100px 24px 120px; overflow: hidden; background: #fff; }
  .hr-hero-blob1 { position: absolute; top: -8%; right: -4%; width: 520px; height: 520px; border-radius: 50%; background: radial-gradient(circle, rgba(167,243,208,0.55), rgba(110,231,183,0.25)); filter: blur(80px); pointer-events: none; }
  .hr-hero-blob2 { position: absolute; bottom: -12%; left: -8%; width: 640px; height: 640px; border-radius: 50%; background: radial-gradient(circle, rgba(147,197,253,0.45), rgba(196,181,253,0.25)); filter: blur(80px); pointer-events: none; }
  .hr-hero-inner { max-width: 1200px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: center; position: relative; z-index: 1; }
  @media (max-width: 900px) { .hr-hero-inner { grid-template-columns: 1fr; } .hr-hero-card { display: none; } }

  .hr-badge { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 999px; background: #f0fdf4; border: 1px solid #bbf7d0; font-size: 13px; font-weight: 500; color: #166534; margin-bottom: 28px; }
  .hr-badge-dot { width: 8px; height: 8px; border-radius: 50%; background: #22c55e; display: inline-block; animation: hr-pulse 2s ease-in-out infinite; }
  @keyframes hr-pulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); } 50% { box-shadow: 0 0 0 6px rgba(34,197,94,0); } }

  .hr-hero h1 { font-size: clamp(1.95rem, 3.75vw, 3.15rem); font-weight: 900; line-height: 1.1; letter-spacing: -0.03em; color: #0f172a; margin-bottom: 24px; }
  .hr-gradient-text { background: linear-gradient(135deg, #059669 0%, #0284c7 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
  .hr-hero-sub { font-size: 1.15rem; color: #475569; line-height: 1.8; margin-bottom: 40px; font-weight: 400; }
  .hr-btn-group { display: flex; gap: 16px; flex-wrap: wrap; }
  .hr-btn-primary { padding: 14px 28px; background: #0f172a; color: #fff; border: none; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; }
  .hr-btn-primary:hover { background: #059669; transform: translateY(-1px); }
  .hr-btn-secondary { padding: 14px 28px; background: #fff; color: #334155; border: 1px solid #e2e8f0; border-radius: 12px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
  .hr-btn-secondary:hover { border-color: #6ee7b7; background: #f0fdf4; color: #059669; }

  /* Hero Card */
  .hr-hero-card { background: #fff; border-radius: 24px; border: 1px solid #e2e8f0; padding: 32px; box-shadow: 0 20px 60px rgba(5,150,105,0.09), 0 4px 16px rgba(0,0,0,0.04); position: relative; }
  .hr-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 20px; border-bottom: 1px solid #f1f5f9; }
  .hr-card-title-label { font-size: 13px; color: #94a3b8; font-weight: 500; margin-bottom: 3px; }
  .hr-card-title-value { font-size: 17px; font-weight: 700; color: #0f172a; }
  .hr-card-icon { width: 48px; height: 48px; background: #dcfce7; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .hr-emp-item { display: flex; justify-content: space-between; align-items: center; padding: 13px 14px; border-radius: 12px; background: #f8fafc; border: 1px solid #f1f5f9; margin-bottom: 10px; }
  .hr-emp-left { display: flex; align-items: center; gap: 12px; }
  .hr-emp-avatar { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .hr-emp-name { font-size: 13.5px; font-weight: 600; color: #334155; }
  .hr-emp-role { font-size: 11.5px; color: #94a3b8; margin-top: 1px; }
  .hr-emp-status { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 999px; }
  .hr-status-active { background: #dcfce7; color: #166534; }
  .hr-status-review { background: #fef9c3; color: #854d0e; }
  .hr-status-new { background: #dbeafe; color: #1e40af; }
  .hr-card-metric { margin-top: 16px; padding: 20px; background: linear-gradient(135deg, #059669, #0284c7); border-radius: 16px; display: flex; justify-content: space-between; align-items: center; }
  .hr-card-metric-label { font-size: 12px; color: rgba(255,255,255,0.8); font-weight: 500; margin-bottom: 4px; }
  .hr-card-metric-value { font-size: 26px; font-weight: 900; color: #fff; letter-spacing: -0.02em; }
  .hr-card-metric-badge { background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25); border-radius: 999px; padding: 5px 12px; font-size: 12px; font-weight: 600; color: #fff; }

  /* ── Stats Bar ── */
  .hr-stats { background: #fff; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; }
  .hr-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); max-width: 1200px; margin: 0 auto; }
  @media (max-width: 768px) { .hr-stats-grid { grid-template-columns: 1fr 1fr; } }
  .hr-stat-item { padding: 36px 24px; text-align: center; border-right: 1px solid #f1f5f9; }
  .hr-stat-item:last-child { border-right: none; }
  @media (max-width: 768px) { .hr-stat-item:nth-child(2n) { border-right: none; } .hr-stat-item { border-bottom: 1px solid #f1f5f9; } }
  .hr-stat-num { font-size: 2.1rem; font-weight: 900; color: #059669; letter-spacing: -0.03em; line-height: 1; margin-bottom: 8px; }
  .hr-stat-label { font-size: 0.82rem; color: #64748b; font-weight: 500; line-height: 1.4; }

  /* ── Shared Section Atoms ── */
  .hr-section { padding: 96px 24px; }
  .hr-container { max-width: 1200px; margin: 0 auto; }
  .hr-section-header { text-align: center; max-width: 720px; margin: 0 auto 64px; }
  .hr-section-header h2 { font-size: clamp(1.8rem, 3.5vw, 2.8rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 16px; line-height: 1.2; }
  .hr-section-header p { font-size: 1.08rem; color: #64748b; line-height: 1.78; }
  .hr-kicker { font-size: 12.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #059669; margin-bottom: 12px; }

  /* ── Services ── */
  .hr-services-bg { background: #f8fafc; border-top: 1px solid rgba(226,232,240,0.6); border-bottom: 1px solid rgba(226,232,240,0.6); }
  .hr-services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 960px) { .hr-services-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .hr-services-grid { grid-template-columns: 1fr; } }
  .hr-svc-card { background: #fff; padding: 36px 32px; border-radius: 20px; border: 1px solid rgba(226,232,240,0.7); box-shadow: 0 1px 4px rgba(0,0,0,0.03); transition: all 0.3s; position: relative; overflow: hidden; cursor: default; }
  .hr-svc-card:hover { box-shadow: 0 16px 48px rgba(0,0,0,0.09); transform: translateY(-3px); border-color: transparent; }
  .hr-svc-card::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; opacity: 0; transition: opacity 0.3s; border-radius: 20px 20px 0 0; }
  .hr-svc-card:hover::before { opacity: 1; }
  .hr-svc-card.c-teal::before   { background: linear-gradient(90deg, #059669, #0d9488); }
  .hr-svc-card.c-blue::before   { background: linear-gradient(90deg, #0284c7, #4f46e5); }
  .hr-svc-card.c-purple::before { background: linear-gradient(90deg, #7c3aed, #db2777); }
  .hr-svc-card.c-amber::before  { background: linear-gradient(90deg, #d97706, #dc2626); }
  .hr-svc-card.c-sky::before    { background: linear-gradient(90deg, #0ea5e9, #06b6d4); }
  .hr-svc-card.c-rose::before   { background: linear-gradient(90deg, #f43f5e, #ec4899); }
  .hr-svc-icon { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 22px; font-size: 24px; }
  .hr-svc-icon.c-teal   { background: #ecfdf5; }
  .hr-svc-icon.c-blue   { background: #eff6ff; }
  .hr-svc-icon.c-purple { background: #f5f3ff; }
  .hr-svc-icon.c-amber  { background: #fffbeb; }
  .hr-svc-icon.c-sky    { background: #e0f2fe; }
  .hr-svc-icon.c-rose   { background: #fff1f2; }
  .hr-svc-card h3 { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin-bottom: 12px; }
  .hr-svc-card p { font-size: 0.91rem; color: #64748b; line-height: 1.72; margin-bottom: 22px; }
  .hr-svc-tags { display: flex; flex-wrap: wrap; gap: 7px; }
  .hr-svc-tag { padding: 4px 11px; font-size: 11px; font-weight: 600; border-radius: 999px; background: rgba(226,232,240,0.6); color: #475569; transition: all 0.2s; }
  .hr-svc-card:hover .hr-svc-tag { background: #f0fdf4; color: #059669; }

  /* ── Feature Spotlight ── */
  .hr-spotlight-bg { background: #fff; }
  .hr-spotlight-inner { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 32px; padding: 72px 64px; display: flex; gap: 80px; align-items: center; flex-wrap: wrap; }
  @media (max-width: 960px) { .hr-spotlight-inner { padding: 48px 40px; gap: 48px; } }
  .hr-spotlight-left { flex: 1; min-width: 280px; }
  .hr-spotlight-left h2 { font-size: clamp(1.8rem, 3vw, 2.5rem); font-weight: 800; color: #0f172a; letter-spacing: -0.02em; margin-bottom: 14px; line-height: 1.22; }
  .hr-spotlight-left > p { font-size: 1.03rem; color: #64748b; line-height: 1.8; margin-bottom: 44px; }
  .hr-feat-list { display: flex; flex-direction: column; gap: 28px; }
  .hr-feat { display: flex; gap: 16px; align-items: flex-start; }
  .hr-feat-icon { flex-shrink: 0; width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .hr-feat-icon.c-teal  { background: #ecfdf5; }
  .hr-feat-icon.c-blue  { background: #eff6ff; }
  .hr-feat-icon.c-amber { background: #fffbeb; }
  .hr-feat h4 { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 5px; }
  .hr-feat p { font-size: 0.9rem; color: #64748b; line-height: 1.68; }
  .hr-spotlight-right { flex: 1; min-width: 280px; }
  .hr-visual-card { background: linear-gradient(145deg, #059669, #0284c7); border-radius: 24px; padding: 36px; position: relative; overflow: hidden; min-height: 390px; display: flex; flex-direction: column; gap: 20px; justify-content: space-between; }
  .hr-visual-dots { position: absolute; inset: 0; opacity: 0.12; background-image: radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 0); background-size: 24px 24px; pointer-events: none; }
  .hr-visual-metric-box { background: rgba(255,255,255,0.15); backdrop-filter: blur(10px); border: 1px solid rgba(255,255,255,0.25); border-radius: 18px; padding: 22px; }
  .hr-vm-label { font-size: 12px; color: rgba(255,255,255,0.75); font-weight: 500; margin-bottom: 6px; }
  .hr-vm-value { font-size: 36px; font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1; }
  .hr-vm-delta { font-size: 12px; color: rgba(255,255,255,0.85); margin-top: 6px; display: flex; align-items: center; gap: 4px; }
  .hr-vm-delta span { background: rgba(255,255,255,0.2); border-radius: 999px; padding: 2px 8px; font-weight: 600; }
  .hr-visual-insight { background: #fff; border-radius: 18px; padding: 22px; box-shadow: 0 8px 32px rgba(0,0,0,0.14); }
  .hr-vi-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
  .hr-vi-title { font-weight: 700; color: #1e293b; font-size: 14px; }
  .hr-vi-badge { font-size: 11px; font-weight: 700; color: #059669; background: #dcfce7; padding: 4px 10px; border-radius: 999px; letter-spacing: 0.04em; }
  .hr-bar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
  .hr-bar-label { font-size: 11.5px; color: #64748b; width: 82px; flex-shrink: 0; }
  .hr-bar-track { flex: 1; height: 7px; background: #f1f5f9; border-radius: 999px; overflow: hidden; }
  .hr-bar-fill { height: 100%; border-radius: 999px; transition: width 1s ease; }
  .hr-bar-val { font-size: 12px; font-weight: 600; color: #334155; width: 32px; text-align: right; }

  /* ── Process ── */
  .hr-process-bg { background: #f8fafc; border-top: 1px solid rgba(226,232,240,0.6); }
  .hr-steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 40px; position: relative; }
  @media (max-width: 768px) { .hr-steps { grid-template-columns: 1fr; } }
  .hr-steps::before { content: ''; position: absolute; top: 32px; left: calc(16.67% + 8px); right: calc(16.67% + 8px); height: 2px; background: linear-gradient(90deg, #059669, #0284c7); z-index: 0; opacity: 0.3; }
  @media (max-width: 768px) { .hr-steps::before { display: none; } }
  .hr-step { text-align: center; position: relative; z-index: 1; }
  .hr-step-num { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #059669, #0284c7); color: #fff; font-size: 1.4rem; font-weight: 900; display: flex; align-items: center; justify-content: center; margin: 0 auto 24px; box-shadow: 0 8px 24px rgba(5,150,105,0.35); }
  .hr-step h3 { font-size: 1.1rem; font-weight: 700; color: #1e293b; margin-bottom: 12px; }
  .hr-step p { font-size: 0.9rem; color: #64748b; line-height: 1.72; max-width: 280px; margin: 0 auto; }

  /* ── Compliance / Trust (dark) ── */
  .hr-trust-bg { background: #0f172a; color: #f8fafc; position: relative; overflow: hidden; }
  .hr-trust-glow1 { position: absolute; top: -5%; right: 20%; width: 600px; height: 600px; background: radial-gradient(circle, rgba(5,150,105,0.22), transparent 70%); pointer-events: none; }
  .hr-trust-glow2 { position: absolute; bottom: -15%; left: -8%; width: 500px; height: 500px; background: radial-gradient(circle, rgba(2,132,199,0.16), transparent 70%); pointer-events: none; }
  .hr-trust-bg .hr-kicker { color: #34d399; }
  .hr-trust-bg h2 { color: #fff; }
  .hr-trust-bg .hr-section-header p { color: #94a3b8; }
  .hr-trust-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
  @media (max-width: 960px) { .hr-trust-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .hr-trust-grid { grid-template-columns: 1fr; } }
  .hr-trust-card { background: rgba(30,41,59,0.5); border: 1px solid rgba(71,85,105,0.4); border-radius: 20px; padding: 32px; transition: border-color 0.3s, background 0.3s; }
  .hr-trust-card:hover { border-color: rgba(5,150,105,0.5); background: rgba(30,41,59,0.75); }
  .hr-trust-icon-wrap { font-size: 28px; margin-bottom: 18px; display: block; }
  .hr-trust-card h4 { font-size: 1.02rem; font-weight: 700; color: #fff; margin-bottom: 10px; }
  .hr-trust-card p { font-size: 0.89rem; color: #94a3b8; line-height: 1.72; }

  /* ── Testimonials ── */
  .hr-testimonials-bg { background: #fff; }
  .hr-testimonials-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
  @media (max-width: 960px) { .hr-testimonials-grid { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .hr-testimonials-grid { grid-template-columns: 1fr; } }
  .hr-tcard { background: #f8fafc; border: 1px solid #f1f5f9; border-radius: 20px; padding: 32px; transition: all 0.3s; display: flex; flex-direction: column; }
  .hr-tcard:hover { background: #fff; box-shadow: 0 14px 44px rgba(0,0,0,0.08); border-color: #e2e8f0; }
  .hr-stars { color: #f59e0b; font-size: 15px; letter-spacing: 2px; margin-bottom: 18px; }
  .hr-tcard-text { font-size: 0.93rem; color: #334155; line-height: 1.78; font-style: italic; margin-bottom: 24px; flex: 1; }
  .hr-tcard-author { display: flex; align-items: center; gap: 12px; }
  .hr-author-avatar { width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .hr-author-name { font-size: 13.5px; font-weight: 700; color: #1e293b; }
  .hr-author-role { font-size: 12px; color: #94a3b8; margin-top: 1px; }

  /* ── CTA ── */
  .hr-cta-bg { background: #fff; border-top: 1px solid #f1f5f9; }
  .hr-cta-inner { background: #0f172a; border-radius: 32px; padding: 88px 56px; text-align: center; position: relative; overflow: hidden; max-width: 1000px; margin: 0 auto; }
  @media (max-width: 640px) { .hr-cta-inner { padding: 52px 28px; border-radius: 24px; } }
  .hr-cta-glow1 { position: absolute; top: -20%; right: -5%; width: 400px; height: 400px; background: radial-gradient(circle, rgba(5,150,105,0.32), transparent 65%); pointer-events: none; }
  .hr-cta-glow2 { position: absolute; bottom: -20%; left: -5%; width: 400px; height: 400px; background: radial-gradient(circle, rgba(2,132,199,0.22), transparent 65%); pointer-events: none; }
  .hr-cta-inner h2 { font-size: clamp(2rem, 4vw, 3rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; line-height: 1.15; margin-bottom: 18px; position: relative; z-index: 1; }
  .hr-cta-inner > p { font-size: 1.12rem; color: #94a3b8; font-weight: 300; max-width: 560px; margin: 0 auto 40px; line-height: 1.75; position: relative; z-index: 1; }
  .hr-cta-btns { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; margin-bottom: 32px; }
  .hr-cta-primary { padding: 15px 34px; background: #059669; color: #fff; border: none; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: background 0.2s, transform 0.15s; box-shadow: 0 4px 20px rgba(5,150,105,0.45); }
  .hr-cta-primary:hover { background: #10b981; transform: translateY(-1px); }
  .hr-cta-secondary { padding: 15px 34px; background: #1e293b; color: #cbd5e1; border: 1px solid #334155; border-radius: 12px; font-size: 16px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .hr-cta-secondary:hover { background: #334155; color: #fff; }
  .hr-cta-trust { display: flex; align-items: center; justify-content: center; gap: 28px; flex-wrap: wrap; position: relative; z-index: 1; }
  .hr-cta-trust-item { display: flex; align-items: center; gap: 6px; font-size: 13px; color: #64748b; }
  .hr-cta-trust-item .check { color: #10b981; font-weight: 800; }

  /* ── Module Detail Pages ── */
  .hr-mod { background: #f8fafc; min-height: 100vh; }
  .hr-mod-inner { max-width: 1100px; margin: 0 auto; padding: 64px 24px 96px; }
  .hr-back-btn { display: inline-flex; align-items: center; gap: 7px; padding: 7px 16px; border-radius: 999px; border: 1px solid #e2e8f0; background: #fff; color: #475569; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s; margin-bottom: 32px; }
  .hr-back-btn:hover { background: #f0fdf4; border-color: #6ee7b7; color: #059669; }
  .hr-mod-kicker { font-size: 12.5px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #059669; margin-bottom: 10px; }
  .hr-mod-title { font-size: clamp(1.5rem, 3vw, 2.4rem); font-weight: 900; color: #0f172a; letter-spacing: -0.03em; margin-bottom: 18px; line-height: 1.12; }
  .hr-mod-desc { font-size: 1.08rem; color: #64748b; line-height: 1.8; max-width: 680px; margin-bottom: 28px; }
  .hr-mod-tags { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 56px; }
  .hr-mod-tag { padding: 5px 14px; border-radius: 999px; background: #ecfdf5; color: #059669; font-size: 12px; font-weight: 600; border: 1px solid #bbf7d0; }
  .hr-mod-section { margin-bottom: 56px; }
  .hr-mod-section h3 { font-size: 1.45rem; font-weight: 800; color: #2563eb; margin-bottom: 20px; letter-spacing: -0.02em; }
  .hr-mod-split { display: grid; grid-template-columns: minmax(0,1.2fr) minmax(0,1fr); gap: 56px; align-items: start; margin-bottom: 56px; }
  @media (max-width: 900px) { .hr-mod-split { grid-template-columns: 1fr; gap: 36px; } }
  .hr-mod-split-title { font-size: 1.55rem; font-weight: 800; color: #0f172a; margin-bottom: 14px; letter-spacing: -0.02em; line-height: 1.25; }
  .hr-mod-split-body { font-size: 0.96rem; color: #64748b; line-height: 1.78; margin-bottom: 28px; }
  .hr-mod-check-list { display: flex; flex-direction: column; gap: 12px; }
  .hr-mod-check { display: flex; align-items: flex-start; gap: 10px; font-size: 0.93rem; color: #334155; line-height: 1.6; }
  .hr-mod-check-icon { color: #059669; font-weight: 800; flex-shrink: 0; font-size: 16px; margin-top: 1px; }
  .hr-mod-right-box { background: #fff; border: 1px solid #e2e8f0; border-radius: 20px; padding: 32px; box-shadow: 0 4px 20px rgba(0,0,0,0.04); }
  .hr-mod-right-box h4 { font-size: 1.05rem; font-weight: 700; color: #0f172a; margin-bottom: 18px; }
  .hr-mod-right-row { display: flex; align-items: center; justify-content: space-between; padding: 13px 0; border-bottom: 1px solid #f1f5f9; }
  .hr-mod-right-row:last-child { border-bottom: none; }
  .hr-mod-right-label { font-size: 13px; color: #475569; font-weight: 500; display: flex; align-items: center; gap: 8px; }
  .hr-mod-right-val { font-size: 13px; font-weight: 700; color: #059669; }
  .hr-mod-cards { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 18px; margin-bottom: 48px; }
  @media (max-width: 900px) { .hr-mod-cards { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .hr-mod-cards { grid-template-columns: 1fr; } }
  .hr-mod-card { background: #fff; border-radius: 16px; border: 1px solid #e2e8f0; padding: 24px 22px; box-shadow: 0 1px 3px rgba(15,23,42,0.04); transition: all 0.2s; }
  .hr-mod-card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(15,23,42,0.09); border-color: #6ee7b7; }
  .hr-mod-card-chip { font-size: 11px; font-weight: 700; color: #059669; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 7px; }
  .hr-mod-card h4 { font-size: 0.98rem; font-weight: 700; color: #0f172a; margin-bottom: 8px; }
  .hr-mod-card p { font-size: 0.87rem; color: #64748b; line-height: 1.65; }
  .hr-mod-scenarios { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 16px; }
  @media (max-width: 900px) { .hr-mod-scenarios { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 600px) { .hr-mod-scenarios { grid-template-columns: 1fr; } }
  .hr-mod-scenario { background: #0f172a; border-radius: 18px; padding: 22px; color: #e5e7eb; box-shadow: 0 10px 28px rgba(15,23,42,0.45); transition: transform 0.18s; }
  .hr-mod-scenario:hover { transform: translateY(-2px) scale(1.01); }
  .hr-mod-scenario-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #86efac; margin-bottom: 6px; }
  .hr-mod-scenario h4 { font-size: 0.97rem; font-weight: 700; margin-bottom: 7px; color: #f1f5f9; }
  .hr-mod-scenario p { font-size: 0.85rem; color: #94a3b8; line-height: 1.62; }
  .hr-mod-faq { border-top: 1px solid #e2e8f0; padding-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
  @media (max-width: 900px) { .hr-mod-faq { grid-template-columns: 1fr; } }
  .hr-mod-faq-item h4 { font-size: 0.96rem; font-weight: 700; color: #0f172a; margin-bottom: 6px; }
  .hr-mod-faq-item p { font-size: 0.88rem; color: #64748b; line-height: 1.65; }
  .hr-mod-cta { background: #0f172a; border-radius: 24px; padding: 56px 48px; text-align: center; position: relative; overflow: hidden; margin-top: 64px; }
  @media (max-width: 600px) { .hr-mod-cta { padding: 40px 24px; } }
  .hr-mod-cta-glow { position: absolute; top: -20%; right: -5%; width: 360px; height: 360px; background: radial-gradient(circle, rgba(5,150,105,0.3), transparent 65%); pointer-events: none; }
  .hr-mod-cta h2 { font-size: clamp(1.6rem, 3vw, 2.4rem); font-weight: 900; color: #fff; letter-spacing: -0.03em; margin-bottom: 14px; position: relative; z-index: 1; }
  .hr-mod-cta p { font-size: 1rem; color: #94a3b8; max-width: 480px; margin: 0 auto 32px; line-height: 1.75; position: relative; z-index: 1; }
  .hr-mod-cta-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }
  .hr-mod-cta-primary { padding: 13px 28px; background: #059669; color: #fff; border: none; border-radius: 11px; font-size: 15px; font-weight: 700; cursor: pointer; transition: background 0.2s; }
  .hr-mod-cta-primary:hover { background: #10b981; }
  .hr-mod-cta-secondary { padding: 13px 28px; background: #1e293b; color: #cbd5e1; border: 1px solid #334155; border-radius: 11px; font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.2s; }
  .hr-mod-cta-secondary:hover { background: #334155; }

  /* Clickable service card cursor */
  .hr-svc-card { cursor: pointer; }
  .hr-svc-card .hr-svc-arrow { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #059669; margin-top: 16px; opacity: 0; transform: translateX(-4px); transition: opacity 0.2s, transform 0.2s; }
  .hr-svc-card:hover .hr-svc-arrow { opacity: 1; transform: translateX(0); }
`;

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-50px' },
  transition: { duration: 0.6 },
};

const services = [
  {
    color: 'c-teal',
    icon: '👥',
    slug: 'employee-management',
    title: 'Employee Management',
    description:
      'Centralize all employee records, org charts, and lifecycle events in one intuitive hub — from onboarding to offboarding and everything in between.',
    tags: ['Org Charts', 'Employee Profiles', 'Offboarding'],
  },
  {
    color: 'c-blue',
    icon: '💰',
    slug: 'payroll-benefits',
    title: 'Payroll & Benefits',
    description:
      'Automate payroll with tax calculations, direct deposits, and benefits administration. Ensure accurate, on-time payments for your entire workforce.',
    tags: ['Auto Payroll', 'Tax Filing', 'Benefits Admin'],
  },
  {
    color: 'c-purple',
    icon: '📈',
    slug: 'performance-tracking',
    title: 'Performance Tracking',
    description:
      'Set measurable goals, run structured performance reviews, and surface growth insights with analytics built for leaders who care about developing talent.',
    tags: ['Goal Setting', '360° Reviews', 'Analytics'],
  },
  {
    color: 'c-amber',
    icon: '⚖️',
    slug: 'compliance-management',
    title: 'Compliance Management',
    description:
      'Stay ahead of labor laws and regulatory requirements with automated alerts, document management, and audit-ready reporting — always current, always compliant.',
    tags: ['Labor Law', 'Audit Logs', 'Document Mgmt'],
  },
  {
    color: 'c-sky',
    icon: '🕐',
    slug: 'time-attendance',
    title: 'Time & Attendance',
    description:
      'Track hours, manage flexible schedules, and streamline time-off requests with smart automation and real-time visibility across your entire team.',
    tags: ['Time Tracking', 'PTO Management', 'Scheduling'],
  },
  {
    color: 'c-rose',
    icon: '🎓',
    slug: 'training-development',
    title: 'Training & Development',
    description:
      'Build custom learning paths, track certifications, and foster continuous growth. Keep your team skilled and motivated with structured development programs.',
    tags: ['Learning Paths', 'Certifications', 'Skill Tracking'],
  },
];

const trustItems = [
  {
    icon: '🔒',
    title: 'SOC 2 Type II Certified',
    description:
      'Your employee data is protected with enterprise-grade security — encrypted at rest and in transit with continuous monitoring.',
  },
  {
    icon: '📋',
    title: 'FLSA & FMLA Compliant',
    description:
      'Built-in compliance for federal and state labor laws, automatically updated whenever regulations change so you never fall behind.',
  },
  {
    icon: '🛡️',
    title: 'GDPR & CCPA Ready',
    description:
      'Full data privacy compliance tools to manage employee consent, data access requests, and deletion workflows with ease.',
  },
  {
    icon: '🔄',
    title: 'Automated Audit Trails',
    description:
      'Every action is logged with tamper-proof audit trails for all HR records and payroll transactions — ready for any audit.',
  },
  {
    icon: '⚡',
    title: '99.9% Uptime SLA',
    description:
      'Mission-critical HR infrastructure with a guaranteed uptime SLA, backed by redundant systems and 24/7 infrastructure monitoring.',
  },
  {
    icon: '🤝',
    title: 'Dedicated HR Partner',
    description:
      'Access certified HR professionals for guidance on complex employment questions, compliance challenges, and sensitive situations.',
  },
];

const testimonials = [
  {
    color: '#059669',
    initials: 'SK',
    name: 'Sarah Kim',
    role: 'CEO, Brightwave Agency',
    text: "\u201cBizServe completely transformed how we handle HR. What used to take a full day each week now takes 20 minutes. It\u2019s absolutely essential for any growing team.\u201d",
  },
  {
    color: '#0284c7',
    initials: 'MR',
    name: 'Marcus Rodriguez',
    role: 'Operations Director, TechBuild Co.',
    text: '"The compliance tools alone are worth every penny. We went from dreading audits to passing them with ease. The automation has saved us from several very costly mistakes."',
  },
  {
    color: '#7c3aed',
    initials: 'AL',
    name: 'Amanda Lee',
    role: 'HR Manager, Flex Retail Group',
    text: '"Onboarding new hires used to take days of back-and-forth. With BizServe, new employees are fully set up in under an hour. Our employee experience scores have never been higher."',
  },
];

/* ─────────────────────────────────────────
   MODULE SUB-PAGES
───────────────────────────────────────── */

interface ModulePageProps { onBack: () => void; }

/* ── 1. Employee Management ── */
const EmployeeManagementPage: React.FC<ModulePageProps> = ({ onBack }) => (
  <section className="hr-mod">
    <div className="hr-mod-inner">
      <button className="hr-back-btn" onClick={onBack}>← Back to HR Overview</button>
      <div className="hr-mod-kicker">Core HR</div>
      <h1 className="hr-mod-title">Your People. Perfectly Organized.</h1>
      <p className="hr-mod-desc">
        Managing a growing team shouldn't mean digging through spreadsheets and email threads.
        BizServe gives you one central place for every employee record, document, and event —
        from the first offer letter to the final exit interview.
      </p>
      <div className="hr-mod-tags">
        {['Employee Profiles','Org Charts','Document Storage','Onboarding','Offboarding','Role-Based Access'].map((t,i) => <span key={i} className="hr-mod-tag">{t}</span>)}
      </div>

      <div className="hr-mod-split">
        <div>
          <div className="hr-mod-section">
            <h3>A single source of truth for your workforce</h3>
            <p className="hr-mod-split-body">
              Scattered data costs you time and creates compliance risk. BizServe centralizes everything:
              personal info, employment history, compensation, documents, and performance — all in one
              searchable, permission-controlled hub your whole HR team can trust.
            </p>
            <div className="hr-mod-check-list">
              {[
                'Rich employee profiles with custom fields and employment history',
                'Interactive org chart that updates automatically as your team changes',
                'Secure document storage with e-signature and version control',
                'Full audit trail of every profile change for compliance',
                'Self-service portal so employees update their own information',
                'Custom fields to capture the data unique to your business',
              ].map((item, i) => (
                <div key={i} className="hr-mod-check">
                  <span className="hr-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="hr-mod-right-box">
            <h4>Employee 360° Profile</h4>
            {[
              { icon: '👤', label: 'Personal & Contact Info',    val: 'Always current' },
              { icon: '📄', label: 'Documents & Contracts',      val: '100% e-signed' },
              { icon: '💼', label: 'Employment History',          val: 'Full timeline' },
              { icon: '💰', label: 'Compensation & Benefits',     val: 'Synced to payroll' },
              { icon: '📈', label: 'Performance Reviews',         val: 'All cycles tracked' },
              { icon: '🎓', label: 'Training & Certifications',   val: 'With expiry alerts' },
            ].map((r, i) => (
              <div key={i} className="hr-mod-right-row">
                <span className="hr-mod-right-label">{r.icon} {r.label}</span>
                <span className="hr-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Everything covered across the employee lifecycle</h3>
        <div className="hr-mod-cards">
          {[
            { chip: 'Onboarding',       title: 'Paperless New Hire Setup',         body: 'Send offer letters, collect e-signatures, provision access, and assign onboarding tasks — all before day one.' },
            { chip: 'Organization',     title: 'Live Org Chart Builder',            body: 'Visualize your reporting structure in real time. Drag and drop to reorganize as your team evolves.' },
            { chip: 'Documents',        title: 'Secure Document Vault',             body: 'Store contracts, policies, I-9s, and W-4s with version history, expiry alerts, and access controls.' },
            { chip: 'Self-Service',     title: 'Employee Self-Service Portal',      body: 'Employees update addresses, emergency contacts, and banking details themselves — reducing HR admin time by 60%.' },
            { chip: 'Offboarding',      title: 'Structured Exit Management',        body: 'Automate equipment returns, access revocation, final pay, and COBRA notices with a step-by-step offboarding workflow.' },
            { chip: 'Access Control',   title: 'Role-Based Permissions',            body: 'Control exactly who sees what — from sensitive salary data to performance notes — with granular permission settings.' },
          ].map((c, i) => (
            <div key={i} className="hr-mod-card">
              <div className="hr-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="hr-mod-scenarios">
          {[
            { label: 'New Hire', title: 'From offer to productive in one day', body: 'A new designer accepts an offer on Monday. By Tuesday morning, their laptop is configured, accounts provisioned, and onboarding checklist is 80% complete — without HR lifting a finger.' },
            { label: 'Reorg',    title: 'Team restructure, zero confusion',     body: 'After a merger, you update 45 reporting lines in the org chart in under 10 minutes. Every employee sees their new manager instantly in the self-service portal.' },
            { label: 'Audit',    title: 'Compliance audit in 20 minutes',       body: 'An auditor requests all signed employment agreements from the last 3 years. You export them in a single click, fully organized and timestamped.' },
          ].map((s, i) => (
            <div key={i} className="hr-mod-scenario">
              <div className="hr-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="hr-mod-faq">
          {[
            { q: 'Can we migrate data from our current HRIS?',            a: 'Yes. BizServe has guided import tools for CSV exports from all major HRIS platforms including BambooHR, Gusto, and ADP. Our onboarding team assists with every migration.' },
            { q: 'How many custom fields can we add to profiles?',         a: 'Unlimited. You can add text, date, dropdown, number, and file-upload fields to any section of the employee profile.' },
            { q: 'Who can see sensitive data like salary information?',    a: 'You control that with role-based permissions. You can restrict salary data to HR admins and direct managers, keeping it hidden from peers and junior staff.' },
            { q: 'Can employees access their own records on mobile?',      a: 'Yes. The BizServe employee self-service app is available on iOS and Android, giving employees 24/7 access to their documents, pay stubs, and profile.' },
          ].map((f, i) => (
            <div key={i} className="hr-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-cta">
        <div className="hr-mod-cta-glow" />
        <h2>Ready to centralize your people data?</h2>
        <p>Start your free 14-day trial and get your entire employee database organized in under an hour.</p>
        <div className="hr-mod-cta-btns">
          <button className="hr-mod-cta-primary">Start free trial →</button>
          <button className="hr-mod-cta-secondary">Schedule a demo</button>
        </div>
      </div>
    </div>
  </section>
);

/* ── 2. Payroll & Benefits ── */
const PayrollBenefitsPage: React.FC<ModulePageProps> = ({ onBack }) => (
  <section className="hr-mod">
    <div className="hr-mod-inner">
      <button className="hr-back-btn" onClick={onBack}>← Back to HR Overview</button>
      <div className="hr-mod-kicker">Payroll &amp; Compensation</div>
      <h1 className="hr-mod-title">Payroll That Runs Itself.</h1>
      <p className="hr-mod-desc">
        Manual payroll is one of the most error-prone, time-consuming tasks a small business faces.
        BizServe automates the entire cycle — from calculating wages and taxes to filing returns and
        distributing pay — so your team gets paid accurately and on time, every time.
      </p>
      <div className="hr-mod-tags">
        {['Automated Payroll','Tax Filing','Direct Deposit','Benefits Admin','Open Enrollment','Payroll Analytics'].map((t,i) => <span key={i} className="hr-mod-tag">{t}</span>)}
      </div>

      <div className="hr-mod-split">
        <div>
          <div className="hr-mod-section">
            <h3>Automate every step of the pay cycle</h3>
            <p className="hr-mod-split-body">
              BizServe handles the full payroll workflow — from pulling approved hours to distributing
              pay stubs and filing quarterly returns. One review, one click, done.
            </p>
            <div className="hr-mod-check-list">
              {[
                'Automatic gross-to-net calculations for every pay type',
                'Federal, state, and local tax withholding and filing (940, 941, W-2)',
                'Same-day and next-day direct deposit options',
                'Unlimited pay schedules — weekly, bi-weekly, semi-monthly, monthly',
                'Off-cycle payroll for bonuses, commissions, and corrections',
                'Payroll sync with QuickBooks, Xero, and major accounting platforms',
              ].map((item, i) => (
                <div key={i} className="hr-mod-check">
                  <span className="hr-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="hr-mod-right-box">
            <h4>Last Payroll Run Summary</h4>
            {[
              { icon: '👥', label: 'Employees Paid',        val: '24 employees' },
              { icon: '💵', label: 'Gross Payroll',          val: '$112,400' },
              { icon: '🏛️', label: 'Taxes Withheld',         val: '$28,600' },
              { icon: '🏦', label: 'Net Deposited',          val: '$83,800' },
              { icon: '⏱️', label: 'Processing Time',        val: '3 minutes' },
              { icon: '✅', label: 'Errors Found',           val: '0' },
            ].map((r, i) => (
              <div key={i} className="hr-mod-right-row">
                <span className="hr-mod-right-label">{r.icon} {r.label}</span>
                <span className="hr-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Payroll and benefits, fully integrated</h3>
        <div className="hr-mod-cards">
          {[
            { chip: 'Payroll',         title: 'Automated Payroll Runs',            body: 'Schedule payroll to run automatically or review and approve with one click. Handles salary, hourly, overtime, and contractor payments.' },
            { chip: 'Tax Compliance',  title: 'Tax Calculation & Filing',          body: 'BizServe calculates, withholds, pays, and files all federal and state payroll taxes — including W-2s and 1099s at year end.' },
            { chip: 'Direct Deposit',  title: 'Same-Day Direct Deposit',           body: 'Employees receive funds same day or next day. Multiple bank accounts supported. Full pay stub access via the employee portal.' },
            { chip: 'Benefits',        title: 'Benefits Administration',           body: 'Manage health, dental, vision, FSA, HSA, and 401(k) plans. Deductions sync automatically with every payroll run.' },
            { chip: 'Enrollment',      title: 'Open Enrollment Workflow',          body: 'Guide employees through annual enrollment with digital forms, plan comparisons, and e-signature — no paper, no confusion.' },
            { chip: 'Analytics',       title: 'Payroll Cost Analytics',            body: 'Track labor costs by department, role, or project. Forecast payroll spend and model the impact of raises and headcount changes.' },
          ].map((c, i) => (
            <div key={i} className="hr-mod-card">
              <div className="hr-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="hr-mod-scenarios">
          {[
            { label: 'Tax Season',  title: 'W-2s delivered, zero stress',             body: 'January arrives and W-2s are already generated, reviewed, and delivered to every employee digitally — with paper copies available on request. No last-minute scramble.' },
            { label: 'Bonus Run',   title: 'Year-end bonuses paid in 10 minutes',     body: 'You approve the bonus amounts in a spreadsheet upload on a Tuesday afternoon. By Friday morning, every employee has received their bonus with the correct tax withholdings.' },
            { label: 'New Benefits', title: 'New health plan, zero confusion',         body: 'You switch health insurance carriers mid-year. BizServe guides each employee through plan selection with digital forms. New deductions apply automatically from the next payroll.' },
          ].map((s, i) => (
            <div key={i} className="hr-mod-scenario">
              <div className="hr-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="hr-mod-faq">
          {[
            { q: 'Does BizServe file our payroll taxes automatically?',       a: 'Yes. BizServe handles all federal and state payroll tax deposits and filings, including 940, 941, W-2, and 1099 forms. You receive confirmation of every filing.' },
            { q: 'Can we run payroll for both employees and contractors?',    a: 'Absolutely. BizServe supports W-2 employees and 1099 contractors in the same payroll run. 1099-NEC forms are generated automatically at year end.' },
            { q: 'What if we make a payroll error?',                         a: 'You can run an off-cycle correction payroll at any time. BizServe recalculates all affected taxes and provides guidance on any required amended filings.' },
            { q: 'Which benefits types are supported?',                      a: 'Health, dental, vision, life insurance, FSA, HSA, HRA, commuter benefits, and 401(k)/403(b) plans — including employer contribution matching.' },
          ].map((f, i) => (
            <div key={i} className="hr-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-cta">
        <div className="hr-mod-cta-glow" />
        <h2>Ready to automate your payroll?</h2>
        <p>Join thousands of small businesses running error-free payroll in minutes, not hours.</p>
        <div className="hr-mod-cta-btns">
          <button className="hr-mod-cta-primary">Start free trial →</button>
          <button className="hr-mod-cta-secondary">Schedule a demo</button>
        </div>
      </div>
    </div>
  </section>
);

/* ── 3. Performance Tracking ── */
const PerformanceTrackingPage: React.FC<ModulePageProps> = ({ onBack }) => (
  <section className="hr-mod">
    <div className="hr-mod-inner">
      <button className="hr-back-btn" onClick={onBack}>← Back to HR Overview</button>
      <div className="hr-mod-kicker">Performance Management</div>
      <h1 className="hr-mod-title">Build a Culture of High Performance.</h1>
      <p className="hr-mod-desc">
        Annual reviews that collect dust aren't performance management — they're missed opportunities.
        BizServe replaces outdated review cycles with continuous, data-driven performance conversations
        that actually develop your team and drive results.
      </p>
      <div className="hr-mod-tags">
        {['Goal Setting','360° Feedback','Review Cycles','Performance Analytics','Development Plans','Calibration'].map((t,i) => <span key={i} className="hr-mod-tag">{t}</span>)}
      </div>

      <div className="hr-mod-split">
        <div>
          <div className="hr-mod-section">
            <h3>From annual reviews to continuous growth</h3>
            <p className="hr-mod-split-body">
              High-performing teams don't wait for December to talk about performance. BizServe enables
              continuous check-ins, real-time feedback, and structured reviews that keep your team
              aligned and growing all year long.
            </p>
            <div className="hr-mod-check-list">
              {[
                'OKR and SMART goal frameworks with progress tracking',
                '360° feedback from peers, direct reports, and managers',
                'Customizable review templates for any cadence or role',
                'Calibration tools to ensure fair, consistent ratings across teams',
                'Performance improvement plans (PIPs) with milestone tracking',
                'Automated nudges so review cycles never go off track',
              ].map((item, i) => (
                <div key={i} className="hr-mod-check">
                  <span className="hr-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="hr-mod-right-box">
            <h4>Team Performance Dashboard</h4>
            {[
              { icon: '🎯', label: 'Goals On Track',             val: '87% of team' },
              { icon: '⭐', label: 'Avg. Performance Score',     val: '4.2 / 5.0' },
              { icon: '💬', label: 'Feedback Given This Month',  val: '142 comments' },
              { icon: '📋', label: 'Reviews Completed',          val: '94% on time' },
              { icon: '📈', label: 'Top Performers Identified',  val: '12 employees' },
              { icon: '🔄', label: 'PIPs In Progress',           val: '2 active' },
            ].map((r, i) => (
              <div key={i} className="hr-mod-right-row">
                <span className="hr-mod-right-label">{r.icon} {r.label}</span>
                <span className="hr-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>A complete performance management toolkit</h3>
        <div className="hr-mod-cards">
          {[
            { chip: 'Goals',         title: 'OKR & Goal Management',              body: 'Set company, team, and individual goals using OKR or SMART frameworks. Track progress in real time with visual dashboards and milestone updates.' },
            { chip: 'Feedback',      title: '360° Feedback Surveys',              body: 'Collect multi-rater feedback from peers, direct reports, and cross-functional partners. Anonymous options encourage honest, actionable insights.' },
            { chip: 'Reviews',       title: 'Flexible Review Cycles',             body: 'Run quarterly, semi-annual, or annual reviews on your schedule. Customize templates by role, department, or seniority level.' },
            { chip: 'Analytics',     title: 'Performance Analytics',              body: 'Spot high performers, identify flight risks, and surface skill gaps with team-level analytics and trend reports across review cycles.' },
            { chip: 'Development',   title: 'Individual Development Plans',       body: 'Build structured IDPs linking performance outcomes to growth goals, training assignments, and promotion readiness criteria.' },
            { chip: 'Calibration',   title: 'Calibration Workflow',               body: 'Bring managers together to align performance ratings before finalization — ensuring fairness and preventing recency bias across teams.' },
          ].map((c, i) => (
            <div key={i} className="hr-mod-card">
              <div className="hr-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="hr-mod-scenarios">
          {[
            { label: 'Review Cycle',   title: 'Q3 reviews done in 2 days',          body: 'You launch a 360° review cycle for 30 employees. Automated reminders push completion to 98%. Calibration is done in a 45-minute session. Results distributed same day.' },
            { label: 'Top Talent',     title: 'High performer retained after offer', body: 'Analytics flag that your best engineer has stalled on two goals. Their manager schedules a development conversation. Two months later, they turn down a competitor offer.' },
            { label: 'Underperformance', title: 'PIP with clarity, not conflict',   body: 'A sales rep misses targets for two quarters. Their PIP is built in BizServe with clear milestones and bi-weekly check-ins. Three months later, they are back on track.' },
          ].map((s, i) => (
            <div key={i} className="hr-mod-scenario">
              <div className="hr-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="hr-mod-faq">
          {[
            { q: 'Can we customize review questions for different roles?',     a: 'Yes. You can create unlimited review templates with custom rating scales, open-text questions, and competency frameworks tailored to each role or department.' },
            { q: 'Is 360° feedback truly anonymous?',                         a: 'You control anonymity settings per review cycle. Peer feedback can be made fully anonymous, or attributed — depending on your team culture and review purpose.' },
            { q: 'How do OKRs connect to performance reviews?',               a: 'Goal progress is automatically pulled into review forms, giving managers and employees a data-driven starting point for every performance conversation.' },
            { q: 'Can managers see historical performance data?',              a: 'Yes. Every manager has access to a full performance history for their direct reports — including past reviews, goal records, and feedback — going back to day one.' },
          ].map((f, i) => (
            <div key={i} className="hr-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-cta">
        <div className="hr-mod-cta-glow" />
        <h2>Start building a high-performance culture.</h2>
        <p>Replace annual reviews with continuous growth. Your team will thank you.</p>
        <div className="hr-mod-cta-btns">
          <button className="hr-mod-cta-primary">Start free trial →</button>
          <button className="hr-mod-cta-secondary">Schedule a demo</button>
        </div>
      </div>
    </div>
  </section>
);

/* ── 4. Compliance Management ── */
const ComplianceManagementPage: React.FC<ModulePageProps> = ({ onBack }) => (
  <section className="hr-mod">
    <div className="hr-mod-inner">
      <button className="hr-back-btn" onClick={onBack}>← Back to HR Overview</button>
      <div className="hr-mod-kicker">Legal &amp; Compliance</div>
      <h1 className="hr-mod-title">Stay Compliant. Automatically.</h1>
      <p className="hr-mod-desc">
        Labor law doesn't stop changing — and a missed deadline or overlooked regulation can cost
        a small business tens of thousands of dollars in fines. BizServe monitors the regulatory
        landscape on your behalf and keeps you audit-ready, every day of the year.
      </p>
      <div className="hr-mod-tags">
        {['FLSA','FMLA','ADA','EEOC','COBRA','State Law Updates','Audit Trails','Risk Alerts'].map((t,i) => <span key={i} className="hr-mod-tag">{t}</span>)}
      </div>

      <div className="hr-mod-split">
        <div>
          <div className="hr-mod-section">
            <h3>Compliance that never sleeps</h3>
            <p className="hr-mod-split-body">
              Our legal team monitors federal and state labor law changes 365 days a year. When
              regulations change, BizServe updates its workflows, alerts, and templates automatically —
              so you don't have to track it yourself.
            </p>
            <div className="hr-mod-check-list">
              {[
                'Real-time monitoring of federal and state labor law changes',
                'Automated COBRA notices with deadline tracking',
                'FMLA and leave eligibility calculations built in',
                'EEO-1 and VETS-4212 report generation',
                'Required poster and notice management with distribution tracking',
                'HR policy library with legally vetted templates updated regularly',
              ].map((item, i) => (
                <div key={i} className="hr-mod-check">
                  <span className="hr-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="hr-mod-right-box">
            <h4>Compliance Health Check</h4>
            {[
              { icon: '📋', label: 'I-9 Forms Current',             val: '100% ✓' },
              { icon: '📜', label: 'Signed Policies on File',        val: '100% ✓' },
              { icon: '⚖️', label: 'FLSA Classifications Reviewed',  val: 'Up to date' },
              { icon: '🔔', label: 'Upcoming Deadlines',             val: '2 this month' },
              { icon: '🗂️', label: 'Audit-Ready Documents',          val: '847 on file' },
              { icon: '🛡️', label: 'Compliance Score',               val: '98 / 100' },
            ].map((r, i) => (
              <div key={i} className="hr-mod-right-row">
                <span className="hr-mod-right-label">{r.icon} {r.label}</span>
                <span className="hr-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Every compliance area, covered</h3>
        <div className="hr-mod-cards">
          {[
            { chip: 'Federal Law',      title: 'FLSA, FMLA & ADA Compliance',      body: 'Built-in rules engine enforces minimum wage, overtime, leave eligibility, and ADA accommodation workflows — updated automatically as laws change.' },
            { chip: 'State Law',        title: 'State-Specific Rule Engine',        body: 'Operating in multiple states? BizServe applies the correct state-level rules for each employee\'s work location — sick leave, pay transparency, and more.' },
            { chip: 'Documents',        title: 'Compliance Document Management',    body: 'Store I-9s, W-4s, signed policies, and arbitration agreements. Expiry alerts fire 90, 30, and 7 days before any document needs renewal.' },
            { chip: 'Audit Trail',      title: 'Tamper-Proof Audit Logs',           body: 'Every HR action — from a salary change to a policy acknowledgment — is logged with timestamp, user, and IP address. Fully immutable for legal proceedings.' },
            { chip: 'Risk Alerts',      title: 'Proactive Compliance Alerts',       body: 'Receive early warnings for classification risks, leave threshold violations, and regulation changes that affect your workforce before they become violations.' },
            { chip: 'Reporting',        title: 'Government Reporting',              body: 'Generate EEO-1, VETS-4212, ACA 1094/1095, and state-specific reports with a single click — formatted to exact agency specifications.' },
          ].map((c, i) => (
            <div key={i} className="hr-mod-card">
              <div className="hr-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="hr-mod-scenarios">
          {[
            { label: 'DOL Audit',        title: 'DOL audit — passed in one afternoon',  body: 'A Department of Labor investigator requests 3 years of payroll records, timesheets, and FLSA classification justifications. You export everything in 20 minutes, fully organized.' },
            { label: 'Law Change',       title: 'New state leave law, zero scramble',   body: 'California expands its paid sick leave requirements. BizServe\'s compliance engine detects the change, updates all affected policies, and notifies your HR team the day the law is signed.' },
            { label: 'I-9 Expiry',       title: 'I-9 renewals handled automatically',   body: '12 employees\' work authorization documents are expiring in the next 90 days. BizServe surfaces each one with a task, deadline, and the correct USCIS renewal form pre-attached.' },
          ].map((s, i) => (
            <div key={i} className="hr-mod-scenario">
              <div className="hr-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="hr-mod-faq">
          {[
            { q: 'How quickly does BizServe update when laws change?',          a: 'Our legal team monitors federal and state legislation daily. System updates are typically deployed within 48 hours of a law being enacted, with HR teams notified via in-app alert and email.' },
            { q: 'Does BizServe handle multi-state compliance?',                a: 'Yes. BizServe applies the correct wage, leave, and notice rules based on each employee\'s work location — automatically, across all 50 states plus Washington D.C.' },
            { q: 'Are I-9 verifications supported within BizServe?',           a: 'Yes. BizServe includes a built-in I-9 workflow with document upload, e-signature, Section 2 completion, and E-Verify integration for electronic employment verification.' },
            { q: 'What happens if a compliance issue is flagged?',              a: 'BizServe creates a compliance task assigned to the relevant HR admin with a deadline, remediation steps, and links to the applicable regulation or policy template.' },
          ].map((f, i) => (
            <div key={i} className="hr-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-cta">
        <div className="hr-mod-cta-glow" />
        <h2>Never miss a compliance deadline again.</h2>
        <p>Let BizServe be your always-on compliance partner — so you can focus on building your business.</p>
        <div className="hr-mod-cta-btns">
          <button className="hr-mod-cta-primary">Start free trial →</button>
          <button className="hr-mod-cta-secondary">Schedule a demo</button>
        </div>
      </div>
    </div>
  </section>
);

/* ── 5. Time & Attendance ── */
const TimeAttendancePage: React.FC<ModulePageProps> = ({ onBack }) => (
  <section className="hr-mod">
    <div className="hr-mod-inner">
      <button className="hr-back-btn" onClick={onBack}>← Back to HR Overview</button>
      <div className="hr-mod-kicker">Time Management</div>
      <h1 className="hr-mod-title">Every Hour. Accounted For.</h1>
      <p className="hr-mod-desc">
        Inaccurate timekeeping costs small businesses an average of 4.5% of gross payroll annually
        through time theft, buddy punching, and manual entry errors. BizServe replaces guesswork
        with real-time, automated time and attendance that syncs directly to payroll.
      </p>
      <div className="hr-mod-tags">
        {['Time Tracking','Shift Scheduling','PTO Management','Leave Requests','Overtime Monitoring','Payroll Sync'].map((t,i) => <span key={i} className="hr-mod-tag">{t}</span>)}
      </div>

      <div className="hr-mod-split">
        <div>
          <div className="hr-mod-section">
            <h3>Accurate time data, automatically</h3>
            <p className="hr-mod-split-body">
              From clock-in to payroll, BizServe handles the entire time management workflow. Employees
              clock in from web, mobile, or a shared kiosk. Managers approve timesheets. Payroll runs
              with the correct hours — no manual entry, no rounding errors.
            </p>
            <div className="hr-mod-check-list">
              {[
                'Web, mobile app, and kiosk clock-in with GPS and photo verification',
                'Automatic overtime calculations for FLSA and state-specific rules',
                'Manager timesheet approval workflow with audit trail',
                'Real-time visibility into who is clocked in across all locations',
                'Approved timesheets sync to payroll with zero manual entry',
                'Custom rounding rules, grace periods, and break deduction policies',
              ].map((item, i) => (
                <div key={i} className="hr-mod-check">
                  <span className="hr-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="hr-mod-right-box">
            <h4>This Week at a Glance</h4>
            {[
              { icon: '🟢', label: 'Currently Clocked In',        val: '18 employees' },
              { icon: '⏰', label: 'Avg Hours This Week',          val: '38.2 hrs' },
              { icon: '⚠️', label: 'Overtime Alerts',              val: '3 flagged' },
              { icon: '🌴', label: 'PTO Requests Pending',         val: '5 awaiting' },
              { icon: '📅', label: 'Open Shifts This Week',        val: '4 unfilled' },
              { icon: '💰', label: 'Payroll Hours Approved',       val: '96% ready' },
            ].map((r, i) => (
              <div key={i} className="hr-mod-right-row">
                <span className="hr-mod-right-label">{r.icon} {r.label}</span>
                <span className="hr-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Time and leave management, completely handled</h3>
        <div className="hr-mod-cards">
          {[
            { chip: 'Tracking',       title: 'Multi-Method Time Tracking',         body: 'Employees can clock in from a web browser, the BizServe mobile app, or a shared tablet kiosk. GPS stamping and selfie verification prevent buddy punching.' },
            { chip: 'Scheduling',     title: 'Drag-and-Drop Shift Scheduling',     body: 'Build weekly schedules visually with a drag-and-drop planner. Set recurring shifts, auto-fill based on availability, and publish to employees with instant notifications.' },
            { chip: 'PTO',            title: 'PTO & Leave Management',             body: 'Configurable PTO policies with accrual rules, carryover caps, and blackout dates. Employees request time off in the app; managers approve in one tap.' },
            { chip: 'Overtime',       title: 'Overtime Monitoring & Alerts',       body: 'Real-time alerts when an employee approaches overtime thresholds. Managers can reassign shifts before overtime is triggered, saving significant labor cost.' },
            { chip: 'Compliance',     title: 'Break & Meal Compliance',            body: 'California, Oregon, and other state-specific break rules are enforced automatically. Missed break alerts fire in real time to keep you compliant.' },
            { chip: 'Payroll Sync',   title: 'One-Click Payroll Integration',      body: 'Approved hours flow directly into payroll — no re-entry, no spreadsheet exports. Works natively with BizServe Payroll and integrates with ADP and Gusto.' },
          ].map((c, i) => (
            <div key={i} className="hr-mod-card">
              <div className="hr-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="hr-mod-scenarios">
          {[
            { label: 'Retail',       title: 'Holiday schedule built in 15 minutes',  body: 'A 40-person retail team needs holiday coverage across 3 locations. The manager uses the drag-and-drop scheduler, balances shifts by availability, and publishes in one click. Zero scheduling conflicts.' },
            { label: 'Overtime',     title: 'Overtime costs cut by 22%',             body: 'After enabling overtime alerts, managers at a logistics company began reassigning shifts before the 40-hour threshold. Labor costs dropped $18,000 in the first quarter.' },
            { label: 'Remote Team',  title: 'Remote team hours tracked reliably',    body: 'A distributed software team across 4 time zones tracks project hours via the mobile app. Managers see real-time hour totals for each sprint and approve timesheets before payroll Monday morning.' },
          ].map((s, i) => (
            <div key={i} className="hr-mod-scenario">
              <div className="hr-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="hr-mod-faq">
          {[
            { q: 'Does BizServe support GPS-verified clock-ins?',                 a: 'Yes. Mobile clock-ins include GPS location stamping and optional selfie photo capture. Admins can set geofence boundaries so employees can only clock in when on-site.' },
            { q: 'How are accrual rules configured for different PTO policies?',  a: 'BizServe supports hourly accrual, per-pay-period accrual, and anniversary-grant policies. Carryover caps, waiting periods, and negative balance rules are all configurable.' },
            { q: 'Can employees swap shifts with each other?',                    a: 'Yes. Employees can request shift swaps from within the app. The manager receives a swap request for approval, and the schedule updates automatically once approved.' },
            { q: 'Does time and attendance integrate with third-party payroll?',  a: 'BizServe integrates natively with ADP Workforce Now, Gusto, QuickBooks Payroll, and Paychex — with approved hours exported automatically in each platform\'s required format.' },
          ].map((f, i) => (
            <div key={i} className="hr-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-cta">
        <div className="hr-mod-cta-glow" />
        <h2>Take control of every hour your team works.</h2>
        <p>Accurate time tracking that flows seamlessly into payroll — set up in under 30 minutes.</p>
        <div className="hr-mod-cta-btns">
          <button className="hr-mod-cta-primary">Start free trial →</button>
          <button className="hr-mod-cta-secondary">Schedule a demo</button>
        </div>
      </div>
    </div>
  </section>
);

/* ── 6. Training & Development ── */
const TrainingDevelopmentPage: React.FC<ModulePageProps> = ({ onBack }) => (
  <section className="hr-mod">
    <div className="hr-mod-inner">
      <button className="hr-back-btn" onClick={onBack}>← Back to HR Overview</button>
      <div className="hr-mod-kicker">Learning &amp; Development</div>
      <h1 className="hr-mod-title">Invest in Your Team's Growth.</h1>
      <p className="hr-mod-desc">
        Companies that invest in learning retain employees 94% longer and see 37% higher
        productivity. BizServe makes it easy to build structured learning programs, track
        progress, and tie development directly to career growth — without an L&amp;D department.
      </p>
      <div className="hr-mod-tags">
        {['Learning Paths','Course Builder','Certifications','Skill Matrix','Progress Analytics','Development Plans'].map((t,i) => <span key={i} className="hr-mod-tag">{t}</span>)}
      </div>

      <div className="hr-mod-split">
        <div>
          <div className="hr-mod-section">
            <h3>Structured development at every level</h3>
            <p className="hr-mod-split-body">
              From new hire training to executive development, BizServe provides the tools to build
              meaningful learning experiences. Assign courses, track completions, and connect skill
              development to performance outcomes — all in one place.
            </p>
            <div className="hr-mod-check-list">
              {[
                'Custom learning paths built around role, department, or career level',
                'Course builder with video, documents, quizzes, and SCORM support',
                'Certification tracking with automatic expiry alerts',
                'Skill matrix mapping competencies to roles and career ladders',
                'Manager-assigned training linked to performance improvement plans',
                'Progress dashboards for employees, managers, and HR admins',
              ].map((item, i) => (
                <div key={i} className="hr-mod-check">
                  <span className="hr-mod-check-icon">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="hr-mod-right-box">
            <h4>Learning Dashboard</h4>
            {[
              { icon: '📚', label: 'Courses Assigned This Month',    val: '64 courses' },
              { icon: '✅', label: 'Completion Rate',                 val: '91%' },
              { icon: '🏆', label: 'Certifications Earned',           val: '18 this quarter' },
              { icon: '⏰', label: 'Avg. Time to Complete',           val: '2.4 hrs' },
              { icon: '📊', label: 'Skills Assessed',                 val: '340 data points' },
              { icon: '🔔', label: 'Cert. Renewals Due (30 days)',    val: '7 expiring' },
            ].map((r, i) => (
              <div key={i} className="hr-mod-right-row">
                <span className="hr-mod-right-label">{r.icon} {r.label}</span>
                <span className="hr-mod-right-val">{r.val}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Everything you need to grow your people</h3>
        <div className="hr-mod-cards">
          {[
            { chip: 'Learning Paths',   title: 'Role-Based Learning Paths',          body: 'Build curated learning journeys for every role — from SDR onboarding to manager development. Employees see a clear roadmap of what to learn and why.' },
            { chip: 'Course Builder',   title: 'No-Code Course Creator',             body: 'Build internal courses using video uploads, PDF documents, quizzes, and interactive checklists. Import SCORM packages from external L&D tools.' },
            { chip: 'Certifications',   title: 'Certification Tracking',             body: 'Track professional certifications and licenses with expiry dates. Automated reminders fire 90, 30, and 7 days before renewal to keep your team current.' },
            { chip: 'Skills',           title: 'Skill Matrix & Gap Analysis',        body: 'Map employee skills against role requirements. Surface gaps across your team and use the data to guide hiring, promotions, and training investments.' },
            { chip: 'Integrations',     title: 'LMS & Content Integrations',         body: 'Connect with LinkedIn Learning, Coursera for Business, and Udemy for Business to assign curated external courses directly within BizServe.' },
            { chip: 'Analytics',        title: 'L&D ROI Analytics',                  body: 'Measure training completion rates, time-to-proficiency, certification pass rates, and the correlation between learning activity and performance scores.' },
          ].map((c, i) => (
            <div key={i} className="hr-mod-card">
              <div className="hr-mod-card-chip">{c.chip}</div>
              <h4>{c.title}</h4>
              <p>{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Real-world scenarios</h3>
        <div className="hr-mod-scenarios">
          {[
            { label: 'Onboarding',       title: 'New hire fully trained in 3 days',     body: 'A new customer success manager completes 8 onboarding modules, passes 3 product quizzes, and earns a company certification — all before their first client call on day 4.' },
            { label: 'Compliance',       title: 'Annual safety training — 100% done',   body: 'A 60-person manufacturing team needs annual OSHA safety training. BizServe assigns the course, sends reminders, and tracks completion. 100% done before the deadline. Certificate of completion auto-generated.' },
            { label: 'Leadership',       title: 'Future leaders identified and grown',  body: 'A CEO builds a 6-month leadership development path for 5 high-potential employees. Each follows the same curated curriculum with weekly check-ins. Three are promoted within a year.' },
          ].map((s, i) => (
            <div key={i} className="hr-mod-scenario">
              <div className="hr-mod-scenario-label">{s.label}</div>
              <h4>{s.title}</h4>
              <p>{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-section">
        <h3>Frequently asked questions</h3>
        <div className="hr-mod-faq">
          {[
            { q: 'Do we need an LMS to use BizServe training features?',          a: 'No. BizServe includes a fully built-in LMS with course creation, assignment, tracking, and reporting. You can also connect external LMS platforms via our integration library.' },
            { q: 'Can we import existing training content?',                       a: 'Yes. BizServe supports SCORM 1.2 and 2004 packages, video uploads (MP4, MOV), PDF documents, and direct links to external content from YouTube, Vimeo, or your intranet.' },
            { q: 'How are certifications tracked when they expire?',               a: 'Each certification has a set validity period. BizServe sends automated renewal reminders to both the employee and their manager at 90, 30, and 7 days before expiry.' },
            { q: 'Can employees access training on mobile?',                       a: 'Yes. The BizServe mobile app supports full course access including video playback, quizzes, and document download — for learning on the go, even offline.' },
          ].map((f, i) => (
            <div key={i} className="hr-mod-faq-item">
              <h4>{f.q}</h4>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="hr-mod-cta">
        <div className="hr-mod-cta-glow" />
        <h2>Turn learning into your competitive advantage.</h2>
        <p>Build a culture of continuous growth that attracts, develops, and retains top talent.</p>
        <div className="hr-mod-cta-btns">
          <button className="hr-mod-cta-primary">Start free trial →</button>
          <button className="hr-mod-cta-secondary">Schedule a demo</button>
        </div>
      </div>
    </div>
  </section>
);

/* ─────────────────────────────────────────
   OVERVIEW (the original landing page content)
───────────────────────────────────────── */
interface OverviewProps {
  onSelectModule: (slug: string) => void;
}

const OverviewPage: React.FC<OverviewProps> = ({ onSelectModule }) => {
  return (
    <div className="hr-page">
      <style>{styles}</style>

      {/* ── Hero ── */}
      <section className="hr-hero">
        <div className="hr-hero-blob1" />
        <div className="hr-hero-blob2" />
        <div className="hr-hero-inner">

          {/* Left copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="hr-badge">
                <span className="hr-badge-dot" />
                Trusted by 5,000+ growing businesses — the #1 HR platform for SMBs
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              HR that works as hard as{' '}
              <span className="hr-gradient-text">your team</span>.
            </motion.h1>

            <motion.p
              className="hr-hero-sub"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              BizServe streamlines every aspect of HR — from hiring and onboarding to
              payroll, compliance, and performance management. Enterprise-grade HR
              built for small businesses that demand results without the complexity.
            </motion.p>

            <motion.div
              className="hr-btn-group"
              initial={{ opacity: 0, y: 22 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <button className="hr-btn-primary">Start free trial →</button>
              <button className="hr-btn-secondary">Watch a 2-min demo</button>
            </motion.div>
          </div>

          {/* Right dashboard card */}
          <motion.div
            className="hr-hero-card"
            initial={{ opacity: 0, x: 44 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="hr-card-header">
              <div>
                <div className="hr-card-title-label">Team Overview</div>
                <div className="hr-card-title-value">Active Employees</div>
              </div>
              <div className="hr-card-icon">👥</div>
            </div>

            {[
              { initials: 'JL', color: '#059669', name: 'Jessica Liu',    role: 'Product Design', status: 'Active',    cls: 'hr-status-active' },
              { initials: 'TM', color: '#0284c7', name: 'Tyler Moore',    role: 'Engineering',    status: 'In Review', cls: 'hr-status-review' },
              { initials: 'PR', color: '#7c3aed', name: 'Priya Rajan',    role: 'Marketing',      status: 'New Hire',  cls: 'hr-status-new'    },
            ].map((emp, i) => (
              <div key={i} className="hr-emp-item">
                <div className="hr-emp-left">
                  <div className="hr-emp-avatar" style={{ background: emp.color }}>{emp.initials}</div>
                  <div>
                    <div className="hr-emp-name">{emp.name}</div>
                    <div className="hr-emp-role">{emp.role}</div>
                  </div>
                </div>
                <span className={`hr-emp-status ${emp.cls}`}>{emp.status}</span>
              </div>
            ))}

            <div className="hr-card-metric">
              <div>
                <div className="hr-card-metric-label">Hours saved this month</div>
                <div className="hr-card-metric-value">47.5 hrs</div>
              </div>
              <div className="hr-card-metric-badge">↑ 18% vs last month</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <div className="hr-stats">
        <div className="hr-stats-grid">
          {[
            { num: '5,000+',  label: 'Small Businesses Served' },
            { num: '50,000+', label: 'Employees Managed' },
            { num: '40%',     label: 'Average Cost Reduction' },
            { num: '10+ hrs', label: 'Saved Per Week, Per Team' },
          ].map((s, i) => (
            <motion.div
              key={i}
              className="hr-stat-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
            >
              <div className="hr-stat-num">{s.num}</div>
              <div className="hr-stat-label">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* ── Services ── */}
      <section className="hr-section hr-services-bg">
        <div className="hr-container">
          <motion.div className="hr-section-header" {...fadeUp}>
            <div className="hr-kicker">HR Solutions</div>
            <h2>Everything you need to manage your people</h2>
            <p>
              A complete HR platform built for small businesses. No complexity, no expensive
              consultants — just powerful tools that work from day one.
            </p>
          </motion.div>

          <div className="hr-services-grid">
            {services.map((svc, i) => (
              <motion.div
                key={i}
                className={`hr-svc-card ${svc.color}`}
                onClick={() => onSelectModule(svc.slug)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
              >
                <div className={`hr-svc-icon ${svc.color}`}>{svc.icon}</div>
                <h3>{svc.title}</h3>
                <p>{svc.description}</p>
                <div className="hr-svc-tags">
                  {svc.tags.map((t, j) => (
                    <span key={j} className="hr-svc-tag">{t}</span>
                  ))}
                </div>
                <div className="hr-svc-arrow">Explore {svc.title} →</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature Spotlight ── */}
      <section className="hr-section hr-spotlight-bg">
        <div className="hr-container">
          <motion.div className="hr-spotlight-inner" {...fadeUp}>
            {/* Left */}
            <div className="hr-spotlight-left">
              <div className="hr-kicker">Why BizServe HR</div>
              <h2>Stop drowning in spreadsheets. Start leading with data.</h2>
              <p>
                Manual HR processes cost small businesses thousands of hours and dollars
                every year. BizServe automates the admin work so you can focus on building
                a great team and a great business.
              </p>
              <div className="hr-feat-list">
                {[
                  {
                    icon: '⚡',
                    color: 'c-teal',
                    title: 'Instant Onboarding',
                    desc: 'Get new hires productive in under an hour with digital offer letters, e-signature, and automated setup checklists.',
                  },
                  {
                    icon: '📊',
                    color: 'c-blue',
                    title: 'Real-Time People Analytics',
                    desc: 'Surface workforce trends, headcount forecasts, and engagement insights through interactive dashboards built for business owners.',
                  },
                  {
                    icon: '🔔',
                    color: 'c-amber',
                    title: 'Proactive Compliance Alerts',
                    desc: 'Automated reminders for renewals, review cycles, and regulatory changes — so you\'re always ahead, never scrambling.',
                  },
                ].map((f, i) => (
                  <div key={i} className="hr-feat">
                    <div className={`hr-feat-icon ${f.color}`}>{f.icon}</div>
                    <div>
                      <h4>{f.title}</h4>
                      <p>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right visual */}
            <div className="hr-spotlight-right">
              <div className="hr-visual-card">
                <div className="hr-visual-dots" />
                <div className="hr-visual-metric-box">
                  <div className="hr-vm-label">Team Productivity Score</div>
                  <div className="hr-vm-value">94.2</div>
                  <div className="hr-vm-delta">
                    <span>↑ 12%</span> vs last quarter
                  </div>
                </div>

                <div className="hr-visual-insight">
                  <div className="hr-vi-header">
                    <span className="hr-vi-title">Department Health</span>
                    <span className="hr-vi-badge">All Systems Go</span>
                  </div>
                  {[
                    { label: 'Engineering', pct: 92, color: '#059669' },
                    { label: 'Marketing',   pct: 87, color: '#0284c7' },
                    { label: 'Operations',  pct: 78, color: '#7c3aed' },
                    { label: 'Sales',       pct: 95, color: '#d97706' },
                  ].map((bar, i) => (
                    <div key={i} className="hr-bar-row">
                      <span className="hr-bar-label">{bar.label}</span>
                      <div className="hr-bar-track">
                        <div
                          className="hr-bar-fill"
                          style={{ width: `${bar.pct}%`, background: bar.color }}
                        />
                      </div>
                      <span className="hr-bar-val">{bar.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="hr-section hr-process-bg">
        <div className="hr-container">
          <motion.div className="hr-section-header" {...fadeUp}>
            <div className="hr-kicker">Getting Started</div>
            <h2>Up and running in 3 simple steps</h2>
            <p>
              No lengthy implementation. No IT team required. Be managing HR with
              BizServe the same day you sign up.
            </p>
          </motion.div>

          <div className="hr-steps">
            {[
              {
                num: '1',
                title: 'Connect Your Business',
                desc: 'Import your team data, sync your existing payroll provider, and configure your org structure in minutes with guided setup wizards.',
              },
              {
                num: '2',
                title: 'Automate Your Workflows',
                desc: 'Select from pre-built HR workflows for onboarding, performance reviews, compliance, and payroll — or fully customize them to match how you work.',
              },
              {
                num: '3',
                title: 'Grow With Confidence',
                desc: 'Access real-time dashboards, proactive compliance alerts, and people analytics that help you make smarter decisions as your team scales.',
              },
            ].map((step, i) => (
              <motion.div
                key={i}
                className="hr-step"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <div className="hr-step-num">{step.num}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Compliance & Security (dark) ── */}
      <section className="hr-section hr-trust-bg">
        <div className="hr-trust-glow1" />
        <div className="hr-trust-glow2" />
        <div className="hr-container" style={{ position: 'relative', zIndex: 1 }}>
          <motion.div className="hr-section-header" {...fadeUp}>
            <div className="hr-kicker">Security &amp; Compliance</div>
            <h2>Enterprise-grade protection for your people data</h2>
            <p>
              Your employees' data is your most sensitive asset. We protect it with
              the same standards used by Fortune 500 companies.
            </p>
          </motion.div>

          <div className="hr-trust-grid">
            {trustItems.map((item, i) => (
              <motion.div
                key={i}
                className="hr-trust-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <span className="hr-trust-icon-wrap">{item.icon}</span>
                <h4>{item.title}</h4>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="hr-section hr-testimonials-bg">
        <div className="hr-container">
          <motion.div className="hr-section-header" {...fadeUp}>
            <div className="hr-kicker">Customer Stories</div>
            <h2>Real businesses. Real results.</h2>
            <p>
              Join thousands of small businesses that have made the switch to smarter HR.
              Here's what they're saying.
            </p>
          </motion.div>

          <div className="hr-testimonials-grid">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                className="hr-tcard"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <div className="hr-stars">★★★★★</div>
                <p className="hr-tcard-text">{t.text}</p>
                <div className="hr-tcard-author">
                  <div className="hr-author-avatar" style={{ background: t.color }}>
                    {t.initials}
                  </div>
                  <div>
                    <div className="hr-author-name">{t.name}</div>
                    <div className="hr-author-role">{t.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="hr-section hr-cta-bg">
        <div className="hr-container">
          <motion.div className="hr-cta-inner" {...fadeUp}>
            <div className="hr-cta-glow1" />
            <div className="hr-cta-glow2" />
            <h2>Ready to transform your HR?</h2>
            <p>
              Start your free 14-day trial today. No credit card required.
              Full access to every feature from day one.
            </p>
            <div className="hr-cta-btns">
              <button className="hr-cta-primary">Start free trial →</button>
              <button className="hr-cta-secondary">Schedule a demo</button>
            </div>
            <div className="hr-cta-trust">
              {['14-day free trial', 'No credit card required', 'Setup in under 10 minutes', 'Cancel anytime'].map((item, i) => (
                <div key={i} className="hr-cta-trust-item">
                  <span className="check">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

/* ─────────────────────────────────────────
   ROUTER SHELL
───────────────────────────────────────── */
const HumanResources: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleSlug = (searchParams.get('module') || '').toLowerCase();

  const handleSelectModule = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('module', slug);
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('module');
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderContent = () => {
    switch (moduleSlug) {
      case 'employee-management':  return <EmployeeManagementPage  onBack={handleBack} />;
      case 'payroll-benefits':     return <PayrollBenefitsPage     onBack={handleBack} />;
      case 'performance-tracking': return <PerformanceTrackingPage onBack={handleBack} />;
      case 'compliance-management':return <ComplianceManagementPage onBack={handleBack} />;
      case 'time-attendance':      return <TimeAttendancePage      onBack={handleBack} />;
      case 'training-development': return <TrainingDevelopmentPage onBack={handleBack} />;
      default:                     return <OverviewPage onSelectModule={handleSelectModule} />;
    }
  };

  return (
    <div className="hr-page">
      <style>{styles}</style>
      {renderContent()}
    </div>
  );
};

export default HumanResources;
